import { Fragment, useEffect, useMemo, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import type {
  DataDictionaryDocument,
  DataDictionaryEnvCatalogue,
  DataDictionaryValue,
  Env,
} from '../types';

const DATA_URL = '/data-dictionary.json';
const ENVS: Env[] = ['test', 'dev', 'uat', 'prod'];

// Preferred per-platform column order for the denormalised matrix.
const GLOSSARY_PLATFORM_ORDER = ['Salesforce', 'Semarchy', 'Creatio', 'Auth0', 'NetSuite'];

function isValidEnv(s: string | undefined): s is Env {
  return s === 'test' || s === 'dev' || s === 'uat' || s === 'prod';
}

// A single platform column's value for a row.
interface PlatformCell {
  field: string;
  type: string;
  requiredDisplay: string;
  requiredBool: boolean;
  length?: string | null;
  restricted?: boolean | null;
  unique?: boolean | null;
  calculated?: boolean | null;
  values?: DataDictionaryValue[];
}

// One row in the wide matrix — either a glossary business attribute or a
// single field not covered by any glossary binding.
interface MatrixRow {
  key: string;
  kind: 'attribute' | 'field';
  label: string;
  subtext: string;
  object: string | null;
  ownerPlatform: string | null;
  cells: Record<string, PlatformCell | null>;
  types: string[];
  requiredAny: boolean;
  search: string;
}

// Binding.required is a free-text string; field.required is boolean | null.
function reqStringToBool(s: string): boolean {
  return /^(yes|true|required|y)$/i.test(s.trim());
}

function boolToDisplay(b: boolean | null): string {
  if (b === true) return 'Yes';
  if (b === false) return 'No';
  return '—';
}

function EnvNav({ current }: { current: Env }) {
  return (
    <nav className="section-envnav" aria-label="data dictionary environments">
      <div className="section-envnav-inner">
        {ENVS.map((e) => (
          <NavLink
            key={e}
            to={`/data-dictionary/${e}`}
            className={e === current ? 'active' : undefined}
          >
            {e.toUpperCase()}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export default function DataDictionary() {
  const { env: envParam } = useParams();
  const env: Env = isValidEnv(envParam) ? envParam : 'test';

  const [doc, setDoc] = useState<DataDictionaryDocument | null>(null);
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    fetch(DATA_URL, { cache: 'no-cache' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as DataDictionaryDocument;
        if (!cancelled) {
          setDoc(json);
          setState('loaded');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setState('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const catalogue = doc?.envs?.[env] ?? null;

  return (
    <div className="page">
      <div className="section-heading">
        <h2>Data Dictionary</h2>
        {doc?.generatedAt && (
          <span className="section-updated" title={doc.source}>
            Snapshot <strong>{doc.generatedAt}</strong>
          </span>
        )}
      </div>
      <p>
        Field-level catalogue and cross-platform glossary for the Synergy data model,
        harvested per environment.
      </p>

      <EnvNav current={env} />

      {state === 'loading' && <p className="tile-loading">Loading catalogue…</p>}

      {state === 'error' && (
        <p className="tile-error">Could not load the data dictionary: {error}</p>
      )}

      {state === 'loaded' && !catalogue && (
        <div className="section-placeholder">
          <p>
            No catalogue published for <strong>{env.toUpperCase()}</strong> yet.
          </p>
          <p className="note">
            The catalogue snapshot currently covers TEST. DEV / UAT / PROD will populate
            as the db-dict exporter runs against those estates.
          </p>
        </div>
      )}

      {state === 'loaded' && catalogue && <MatrixTable catalogue={catalogue} />}

      {doc?.note && <p className="dd-note-foot note">{doc.note}</p>}
    </div>
  );
}

// The validation/constraint flags for a cell: open/closed (picklists), unique,
// read-only (formula). Required has its own column so it's not repeated here.
function constraintFlags(cell: PlatformCell): string[] {
  const flags: string[] = [];
  if (cell.restricted === true) flags.push('closed');
  else if (cell.restricted === false) flags.push('open');
  if (cell.unique) flags.push('unique');
  if (cell.calculated) flags.push('read-only');
  return flags;
}

// A Type cell: the data type + its size (max length, "precision,scale", or digits)
// + constraint flags, and for picklist/enum fields an expandable list of the allowed
// domain values (raw value shown in the title when it differs from the label).
function TypeCell({ cell }: { cell: PlatformCell }) {
  const size = cell.length ? ` (${cell.length})` : '';
  const flags = constraintFlags(cell);
  const flagEl =
    flags.length > 0 ? <span className="dd-flags"> · {flags.join(' · ')}</span> : null;
  const values = cell.values;
  if (!values || values.length === 0) {
    return (
      <>
        {cell.type}
        {size}
        {flagEl}
      </>
    );
  }
  return (
    <details className="dd-values">
      <summary>
        {cell.type}
        {size} <span className="dd-values-count">({values.length})</span>
        {flagEl}
      </summary>
      <ul className="dd-values-list">
        {values.map((v) => (
          <li key={v.value} title={v.label && v.label !== v.value ? v.value : undefined}>
            {v.label || v.value}
          </li>
        ))}
      </ul>
    </details>
  );
}

function MatrixTable({ catalogue }: { catalogue: DataDictionaryEnvCatalogue }) {
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('all');
  const [object, setObject] = useState('all');
  const [dataType, setDataType] = useState('all');
  const [onlyRequired, setOnlyRequired] = useState(false);

  // Stable per-platform columns: preferred order, restricted to platforms
  // present anywhere (glossary bindings + harvested platforms); extras appended.
  const platforms = useMemo(() => {
    const present = new Set<string>();
    for (const p of catalogue.platforms) present.add(p.name);
    for (const t of catalogue.glossary) for (const b of t.bindings) present.add(b.platform);
    const ordered = GLOSSARY_PLATFORM_ORDER.filter((p) => present.has(p));
    for (const p of present) if (!ordered.includes(p)) ordered.push(p);
    return ordered;
  }, [catalogue]);

  // Flatten glossary attribute rows + unmapped field rows once.
  const allRows = useMemo<MatrixRow[]>(() => {
    const platIndex = new Map(platforms.map((p, i) => [p, i] as const));

    const finalise = (cells: Record<string, PlatformCell | null>, label: string) => {
      const types = new Set<string>();
      const fieldNames: string[] = [];
      const valueText: string[] = [];
      let requiredAny = false;
      for (const p of platforms) {
        const c = cells[p];
        if (!c) continue;
        if (c.type) types.add(c.type);
        if (c.requiredBool) requiredAny = true;
        fieldNames.push(c.field);
        if (c.values) for (const v of c.values) valueText.push(v.label || v.value);
      }
      return {
        types: Array.from(types),
        requiredAny,
        search: `${label} ${fieldNames.join(' ')} ${valueText.join(' ')}`.toLowerCase(),
      };
    };

    // Mapped keys: "<platform> <bareFieldToken lowercased>" for every binding.
    const mapped = new Set<string>();
    for (const t of catalogue.glossary) {
      for (const b of t.bindings) {
        for (const part of b.field.split(' / ')) {
          const bare = part.split(' (')[0].trim();
          if (bare) mapped.add(`${b.platform} ${bare.toLowerCase()}`);
        }
      }
    }

    // 1) Attribute rows (one per glossary term).
    const attrRows: MatrixRow[] = catalogue.glossary.map((t) => {
      const cells: Record<string, PlatformCell | null> = {};
      for (const p of platforms) {
        const b = t.bindings.find((x) => x.platform === p);
        cells[p] = b
          ? {
              field: b.field,
              type: b.type,
              requiredDisplay: b.required,
              requiredBool: reqStringToBool(b.required),
              length: b.length,
              restricted: b.restricted,
              values: b.values,
            }
          : null;
      }
      const derived = finalise(cells, t.name);
      return {
        key: `attr:${t.name}`,
        kind: 'attribute',
        label: t.name,
        subtext: t.systemOfRecord
          ? `business attribute · ${t.systemOfRecord}`
          : 'business attribute',
        object: null,
        ownerPlatform: null,
        cells,
        ...derived,
      };
    });

    // 2) Field rows (one per unmapped field), grouped platform → object → field.
    const fieldRows: MatrixRow[] = [];
    for (const p of catalogue.platforms) {
      for (const o of p.objects) {
        for (const f of o.fields) {
          const key = `${p.name} ${f.apiName.toLowerCase()}`;
          if (mapped.has(key)) continue;
          const cells: Record<string, PlatformCell | null> = {};
          for (const pl of platforms) cells[pl] = null;
          cells[p.name] = {
            field: f.apiName,
            type: f.dataType,
            requiredDisplay: boolToDisplay(f.required),
            requiredBool: f.required === true,
            length: f.length,
            restricted: f.restricted,
            unique: f.unique,
            calculated: f.calculated,
            values: f.values,
          };
          const derived = finalise(cells, f.apiName);
          fieldRows.push({
            key: `field:${p.name}:${o.apiName}:${f.apiName}`,
            kind: 'field',
            label: f.apiName,
            subtext: `${p.name} · ${o.label || o.apiName}`,
            object: o.apiName,
            ownerPlatform: p.name,
            cells,
            ...derived,
          });
        }
      }
    }

    fieldRows.sort((a, b) => {
      const pa = (platIndex.get(a.ownerPlatform ?? '') ?? 0) -
        (platIndex.get(b.ownerPlatform ?? '') ?? 0);
      if (pa !== 0) return pa;
      if (a.object !== b.object) return (a.object ?? '') < (b.object ?? '') ? -1 : 1;
      if (a.label !== b.label) return a.label < b.label ? -1 : 1;
      return 0;
    });

    return [...attrRows, ...fieldRows];
  }, [catalogue, platforms]);

  // Object dropdown options depend on the selected platform (field rows only).
  const objectOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of allRows) {
      if (r.kind !== 'field' || !r.object) continue;
      if (platform !== 'all' && r.ownerPlatform !== platform) continue;
      set.add(r.object);
    }
    return Array.from(set).sort();
  }, [allRows, platform]);

  const dataTypeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of allRows) {
      if (platform !== 'all' && !r.cells[platform]) continue;
      for (const t of r.types) set.add(t);
    }
    return Array.from(set).sort();
  }, [allRows, platform]);

  // Reset the object filter when it's no longer valid for the chosen platform.
  useEffect(() => {
    if (object !== 'all' && !objectOptions.includes(object)) setObject('all');
  }, [object, objectOptions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows.filter((r) => {
      if (platform !== 'all' && !r.cells[platform]) return false;
      if (object !== 'all') {
        if (r.kind === 'attribute') return false;
        if (r.object !== object) return false;
      }
      if (dataType !== 'all' && !r.types.includes(dataType)) return false;
      if (onlyRequired && !r.requiredAny) return false;
      if (q && !r.search.includes(q)) return false;
      return true;
    });
  }, [allRows, search, platform, object, dataType, onlyRequired]);

  return (
    <div className="dd-panel">
      <div className="dd-controls">
        <input
          type="search"
          className="dd-input"
          placeholder="Search attribute or field…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search rows"
        />
        <select
          className="dd-select"
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          aria-label="Filter by platform"
        >
          <option value="all">All platforms</option>
          {platforms.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <select
          className="dd-select"
          value={object}
          onChange={(e) => setObject(e.target.value)}
          aria-label="Filter by object"
        >
          <option value="all">All objects</option>
          {objectOptions.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <select
          className="dd-select"
          value={dataType}
          onChange={(e) => setDataType(e.target.value)}
          aria-label="Filter by data type"
        >
          <option value="all">All types</option>
          {dataTypeOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <label className="dd-check">
          <input
            type="checkbox"
            checked={onlyRequired}
            onChange={(e) => setOnlyRequired(e.target.checked)}
          />
          Required
        </label>
        <p className="dd-count">
          <strong>{filtered.length}</strong> of {allRows.length} rows
        </p>
      </div>

      {filtered.length === 0 ? (
        <p className="dd-empty">No rows match the current filters.</p>
      ) : (
        <div className="rich-table-wrap dd-table-scroll">
          {/* Denormalised matrix: attribute + field rows down, Field/Type/Required per platform across. */}
          <table className="rich-table dd-table dd-glossary-table">
            <thead>
              <tr>
                <th rowSpan={2}>Attribute / Field</th>
                {platforms.map((p) => (
                  <th key={p} colSpan={3} className="dd-plat-group">
                    {p}
                  </th>
                ))}
              </tr>
              <tr>
                {platforms.map((p) => (
                  <Fragment key={p}>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Required</th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.key}>
                  <td>
                    {r.kind === 'field' ? (
                      <span className="rich-mono">{r.label}</span>
                    ) : (
                      <strong>{r.label}</strong>
                    )}
                    <span className="dd-sub">{r.subtext}</span>
                  </td>
                  {platforms.map((p) => {
                    const c = r.cells[p];
                    return (
                      <Fragment key={p}>
                        <td>{c ? <span className="rich-mono">{c.field}</span> : '—'}</td>
                        <td>{c ? <TypeCell cell={c} /> : '—'}</td>
                        <td>{c ? c.requiredDisplay : '—'}</td>
                      </Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
